import * as React from 'react';

import { find, minBy, some } from 'lodash';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { LONG_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import type { DecoratedEnterpriseContract } from 'bundles/enterprise-admin-account/constants/types';
import {
  isFreeTrialContract,
  isInvoiceContract,
  isUnpaidInvoiceContract,
} from 'bundles/enterprise-admin-account/utils/AdminDashboardAccountUtils';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { EnterpriseAdminNotificationStorage } from 'bundles/enterprise-admin-account/utils/EnterpriseAdminNotificationStorage';
import {
  CONTRACT_STATE_TYPES,
  wasFreeTrialContract,
} from 'bundles/enterprise-admin-contracts/utils/AdminContractUtils';
import {
  getAccountLink,
  getMessagesNewLink,
  getUsersLink,
} from 'bundles/enterprise-admin-navigation/utils/AdminNavigationUtils';

import _t from 'i18n!nls/admin-dashboard';

export const NOTIFICATION_IDS = {
  CC_PAYMENT_PAST_DUE: 'CC_PAYMENT_PAST_DUE',
  CC_EXPIRING: 'CC_EXPIRING',
  INVOICE_PAYMENT_PENDING: 'INVOICE_PAYMENT_PENDING',
  INVOICE_PAYMENT_PAST_DUE: 'INVOICE_PAYMENT_PAST_DUE',
  OVERUSED: 'OVERUSED',
  RENEW: 'RENEW',
  UPSELL: 'UPSELL',
  INVOICE_PAYMENT_SETTLED: 'INVOICE_PAYMENT_SETTLED',
  FREE_TRIAL_INVITATIONS: 'FREE_TRIAL_INVITATIONS',
  FREE_TRIAL_JOINS: 'FREE_TRIAL_JOINS',
  FREE_TRIAL_ENROLLMENTS: 'FREE_TRIAL_ENROLLMENTS',
  FREE_TRIAL_PROGRESS: 'FREE_TRIAL_PROGRESS',
  FREE_TRIAL_CANCELLED: 'FREE_TRIAL_CANCELLED',
  FREE_TRIAL_CONVERTED: 'FREE_TRIAL_CONVERTED',
} as const;

// Suppress the notification for 7 days
export const NOTIFICATION_DISMISS_PERIOD = 7 * 86400 * 1000;

export type NotificationId = keyof typeof NOTIFICATION_IDS;

export type NotificationData = {
  id: NotificationId;
  severity: 'error' | 'warning' | 'success' | 'information';
  priority: number;
  contractCard?: {
    title?: string;
    description: React.ReactChild;
    buttonLabel?: string;
  };
  homePage?: {
    title: string;
    description: React.ReactChild;
    buttonLabel?: string;
  };
  optionalParams?: {
    hostedInvoiceUrl?: string;
    userCount?: number;
  };
};

/**
 * Store options used by getContractNotification
 */
export type NotificationOptions = {
  hasActiveProgram?: boolean;
};

export function getContractNotification(
  contract: DecoratedEnterpriseContract,
  options?: NotificationOptions
): NotificationData | null {
  const isTerminated = contract.contractState === CONTRACT_STATE_TYPES.TERMINATED;
  const isUnpaid = isUnpaidInvoiceContract(contract);
  const isInvoice = isInvoiceContract(contract);
  const now = new Date(Date.now()).valueOf();
  const past72Hours = now - 3600000 * 72;

  // merge with default options
  const defaultOptions: NotificationOptions = {
    hasActiveProgram: true,
  };
  const opts = { ...defaultOptions, ...options };

  // Don't show any notifications for terminated contracts
  // TODO: since we want to show the invoice link, so contracts in CREATED state shouldn't be shortcut here.
  // I put the test here only to fix the js error. And it will be removed when EMPLOYER-7066 is being worked on.
  if (isUnpaid || isTerminated || contract.isRefunded) {
    return null;
  }

  const sentInvoice = find(contract.previousPayments, (prevPayment) => {
    if (prevPayment.invoiceState === 'AWAITING_PAYMENT') {
      return true;
    }
    return false;
  });

  const isFreeTrial = isFreeTrialContract(contract);
  if (isFreeTrial) {
    const isCancelled = !contract.nextPaymentInformation;
    const freeTrialEndsAt = contract.terms[0].endsAt;

    if (isCancelled) {
      const description = (
        <FormattedMessage
          message={_t(
            `Continue your team's access to Coursera by turning on auto-renew before your free trial ends on {endDate}.`
          )}
          endDate={formatDateTimeDisplay(freeTrialEndsAt, LONG_DATE_ONLY_DISPLAY)}
        />
      );
      const buttonLabel = _t('Enable auto-renew');
      return {
        id: 'FREE_TRIAL_CANCELLED',
        severity: 'information',
        priority: 70,
        homePage: {
          title: _t('Coursera for Business will no longer auto-renew'),
          description,
          buttonLabel,
        },
        contractCard: {
          title: _t('Coursera for Business will no longer auto-renew'),
          description,
          buttonLabel,
        },
      };
    } else if (opts.hasActiveProgram) {
      const message = _t(
        'To get the most out of your free trial, invite your learners, send reminders to join and enroll in courses, and track your team’s progress.'
      );
      const buttonLabel = _t('Manage learners');
      return {
        id: 'FREE_TRIAL_INVITATIONS',
        severity: 'information',
        priority: 70,
        homePage: {
          title: _t('Welcome to your Coursera for Business free trial'),
          description: message,
          buttonLabel,
        },
      };
    } else {
      return null;
    }
  }

  const wasFreeTrial = wasFreeTrialContract(contract);
  if (wasFreeTrial) {
    // Find out the conversion date
    const conversionDate = contract.terms[contract.terms.length - 1].endsAt;
    if (conversionDate > past72Hours) {
      return {
        id: 'FREE_TRIAL_CONVERTED',
        severity: 'information',
        priority: 40,
        homePage: {
          title: _t('You can add more licenses now'),
          description: _t('Continue growing your team and your business by adding more licenses'),
          buttonLabel: _t('Add licenses'),
        },
        contractCard: {
          title: _t('You can add more licenses now'),
          description: _t('Continue growing your team and your business by adding more licenses'),
          buttonLabel: _t('Add licenses'),
        },
      };
    }
  }

  if (sentInvoice) {
    const _dueAt = sentInvoice.invoice?.dueAt;
    const invoiceDate = new Date(_dueAt || 0);
    invoiceDate.setHours(0, 0, 0, 0);
    const isOverdue = now > invoiceDate.valueOf();
    const hostedInvoiceUrl = sentInvoice?.invoice?.hostedInvoiceUrl;

    if (isOverdue && _dueAt) {
      return {
        id: 'INVOICE_PAYMENT_PAST_DUE',
        severity: 'warning',
        priority: 10,
        contractCard: {
          title: _t('Your invoice payment is past due'),
          description: _t('Please pay your invoice to invite learners.'),
          buttonLabel: _t('Pay Invoice'),
        },
        homePage: {
          title: _t('Your invoice payment is past due'),
          description: _t('Please pay your invoice to invite learners'),
          buttonLabel: _t('Pay Invoice'),
        },
        optionalParams: {
          hostedInvoiceUrl,
        },
      };
    } else {
      return {
        id: 'INVOICE_PAYMENT_PENDING',
        severity: 'information',
        priority: 30,
        contractCard: {
          description: _t('Your invoice is ready for payment'),
          buttonLabel: _t('Pay Invoice'),
        },
        homePage: {
          title: _t('Your invoice is ready for payment'),
          description: _t('Go to Account & Billing  for more details'),
          buttonLabel: _t('Pay Invoice'),
        },
        optionalParams: {
          hostedInvoiceUrl,
        },
      };
    }
  }

  // Credit card payment past due
  // ** An invoice past due should be terminated. **
  if (contract.nextPaymentInformation?.isPaymentDue) {
    const dueAt = formatDateTimeDisplay(
      contract.nextPaymentInformation?.dueAt + 30 * 86400 * 1000,
      LONG_DATE_ONLY_DISPLAY
    );
    return {
      id: 'CC_PAYMENT_PAST_DUE',
      severity: 'error',
      priority: 10,
      contractCard: {
        title: _t('Your last payment failed'),
        description: _t('Please update your payment method before you grace period ends on #{dueAt}.', {
          dueAt,
        }),
        buttonLabel: _t('Update payment method'),
      },
      homePage: {
        title: _t('Your last payment failed'),
        description: _t('Please visit the billing page for more details'),
        buttonLabel: _t('Update payment method'),
      },
    };
  }

  // Billing information about to expire
  if (contract.nextPaymentInformation?.willPaymentMethodExpireBeforePaymentDate) {
    return {
      id: 'CC_EXPIRING',
      severity: 'warning',
      priority: 20,
      contractCard: {
        title: _t('Your credit card will expire soon'),
        description: _t('Please update your billing details.'),
        buttonLabel: _t('Update payment method'),
      },
      homePage: {
        title: _t('Your credit card will expire soon'),
        description: _t('Please update your billing information.'),
        buttonLabel: _t('View account details'),
      },
    };
  }

  // utilization greater than num of licenses of next term
  if (
    contract.autoRenewInformation.renewsAt &&
    contract.autoRenewInformation.userLimit &&
    contract.utilization &&
    contract.autoRenewInformation.userLimit < contract.utilization.total - contract.utilization.remaining
  ) {
    const title = _t('You are currently using more than #{numLicenses} licenses', {
      numLicenses: contract.autoRenewInformation.userLimit,
    });

    const description = (
      <FormattedMessage
        message={_t(
          'When your contract renews on {renewDate}, {numRemoval} {numRemoval, plural, =1 {learner} other {learners}} will be automatically downgraded to audit mode. If you’d like to choose specific learners to downgrade, remove those learners before your contract renews.'
        )}
        renewDate={formatDateTimeDisplay(contract.autoRenewInformation.renewsAt, LONG_DATE_ONLY_DISPLAY)}
        numRemoval={
          contract.utilization.total - contract.utilization.remaining - contract.autoRenewInformation.userLimit
        }
      />
    );

    return {
      id: 'OVERUSED',
      severity: 'warning',
      priority: 30,
      contractCard: {
        title,
        description,
        buttonLabel: 'Manage users',
      },
      homePage: {
        title,
        description,
        buttonLabel: 'Manage users',
      },
    };
  }

  // Renew notification
  const { terms, autoRenewInformation } = contract;
  const latestTerm = terms[0];
  const today = new Date(Date.now()).valueOf();
  if (latestTerm.endsAt - today < 86400 * 30 * 1000 && !autoRenewInformation.renewsAt) {
    return {
      id: 'RENEW',
      severity: 'information',
      priority: 40,
      contractCard: {
        description: _t('Your contract ends soon.'),
        buttonLabel: _t('Renew contract'),
      },
      homePage: {
        title: _t('Your contract ends soon'),
        description: _t(
          "Renew now and your team will continue to have access to courses from the world's best universities"
        ),
        buttonLabel: _t('View account details'),
      },
    };
  }

  // Upsell
  if (contract.utilization && contract.utilization.remaining === 0) {
    return {
      id: 'UPSELL',
      severity: 'information',
      priority: 50,
      contractCard: {
        title: _t('You have used all of your licenses'),
        description: _t('Add new licenses now to continue growing your team and your business.'),
        buttonLabel: _t('Add licenses'),
      },
      homePage: {
        title: _t('You have used all of your licenses'),
        description: _t('Add new licenses now to continue growing your team and your business.'),
        buttonLabel: _t('View account details'),
      },
    };
  }

  if (
    isInvoice &&
    some(
      contract.previousPayments,
      (payment) =>
        payment.invoice && payment.invoice.invoiceState === 'PAID' && payment.invoice.stateLastChangedAt > past72Hours
    )
  ) {
    return {
      id: 'INVOICE_PAYMENT_SETTLED',
      severity: 'success',
      priority: 60,
      contractCard: undefined,
      homePage: {
        title: _t('You can invite learners now'),
        description: _t('Your payment was successfully processed'),
        buttonLabel: _t('Invite learners'),
      },
    };
  }

  return null;
}

/**
 *
 * @param contracts A list of contracts.
 */
export function getNotificationFromMultipleContracts(
  contracts: DecoratedEnterpriseContract[],
  options?: NotificationOptions
) {
  // get notification for each contract
  const pairs = contracts.map((contract) => ({
    contract,
    notification: getContractNotification(contract, options),
  }));

  // Filter out contracts without notifications
  const nonNullPairs = pairs.filter((p) => Boolean(p.notification)) as {
    contract: DecoratedEnterpriseContract;
    notification: NotificationData;
  }[];

  // Filter out dismissed notifications
  const nonDismissedPairs = nonNullPairs.filter(({ contract, notification }) => {
    return !EnterpriseAdminNotificationStorage.isDismissed(contract.id, notification.id);
  });

  // Reverse the list of notifications so that in case of two notifications have the same priority,
  // the newer one (the one with greater index in `nonDismissedPairs`) gets returned.
  // Note this will mutate `nonDismissedPairs`.
  nonDismissedPairs.reverse();

  // find the most prioritized one, lower value is a higher priority
  return minBy(nonDismissedPairs, (p) => p.notification.priority);
}

export const handleNotificationButtonClick = (
  router: InjectedRouter,
  notificationId: keyof typeof NOTIFICATION_IDS
) => {
  if (notificationId === NOTIFICATION_IDS.INVOICE_PAYMENT_SETTLED || notificationId === NOTIFICATION_IDS.OVERUSED) {
    router.push(getUsersLink(router.params));
  } else if (notificationId === NOTIFICATION_IDS.FREE_TRIAL_INVITATIONS) {
    router.push(getUsersLink(router.params, { presetId: 'invited' }));
  } else if (notificationId === NOTIFICATION_IDS.FREE_TRIAL_JOINS) {
    router.push(getMessagesNewLink(router.params, { presetId: 'remind-join' }));
  } else if (notificationId === NOTIFICATION_IDS.FREE_TRIAL_ENROLLMENTS) {
    router.push(getMessagesNewLink(router.params, { presetId: 'remind-enroll' }));
  } else if (notificationId === NOTIFICATION_IDS.FREE_TRIAL_PROGRESS) {
    router.push(getMessagesNewLink(router.params, { presetId: 'remind-complete' }));
  } else {
    router.push(getAccountLink(router.params));
  }
};
